// Pure helpers lifted out of PurchasingQueuePage.js at Session 79.14c, as the
// first step of that file's decomposition (3,887 lines, 26 top-level
// declarations). Nothing here touches the database, React state or the DOM, so
// every one of them is testable in isolation — which is the whole argument for
// taking them out first. See purchasingQueue.test.js.
//
// needsSpendApproval is the reason this file exists rather than being deferred:
// it decides whether an order needs an executive's approval, it had FOUR call
// sites and NO test, and extraction converts untested money logic into tested
// money logic without changing a line of its behaviour.
//
// fmtDate deliberately did NOT come here. The copy in PurchasingQueuePage.js was
// behaviourally identical to the one already exported from lib/utils.js — same
// locale, same options, same em dash for a missing value — so it was deleted and
// the existing shared one imported. Adding a second shared copy would have made
// this file part of the problem it is fixing. Note that the app carries FOUR
// DIFFERENT fmtDate behaviours under one name across 14 files; that is #35's
// family and is not settled here.

import { itemUom } from './requestItems';

// #256. The Est. arrival field is a native date input, which only accepts
// yyyy-mm-dd. Built from local date parts rather than toISOString().slice(0,10)
// so a stored timestamp cannot land on the previous day once UTC is applied.
export function toDateInputValue(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// 74p. Seeded price fields rendered as "725.5" because Postgres numeric comes
// back unpadded. Format on seed only - never on change, or typing "7.5" becomes
// "7.50" mid-keystroke and fights the user.
export function toMoneyInput(v) {
  if (v == null || v === '') return '';
  const n = parseFloat(v);
  return Number.isNaN(n) ? '' : n.toFixed(2);
}

// #256. Both Mark Ordered paths gated purely on orderTotal > thresholdAmount with
// no awareness of an approval that already happened, so a request approved once
// and then resubmitted returned to pending_purchase_approval forever. The rule,
// from how SAP Ariba, Coupa and ServiceNow all handle a post-approval change: an
// increase re-triggers approval, at-or-below the approved amount holds. Stede's
// gate is an absolute dollar threshold rather than a percentage tolerance by
// decision - one number for Jennifer to understand instead of two. With no prior
// approval, approvedAmount is null and this reduces exactly to the original
// behaviour.
export function needsSpendApproval(settings, orderTotal, approvedAmount) {
  if (!settings?.purchase_approval_threshold_enabled) return false;
  if (orderTotal == null) return false;
  const thresholdAmount = parseFloat(settings?.purchase_approval_threshold_amount) || 0;
  if (orderTotal <= thresholdAmount) return false;
  if (approvedAmount == null) return true;
  return orderTotal > parseFloat(approvedAmount);
}

// An identical copy of this lives in InvoicingPage.js — verified byte-for-byte at
// 79.14c. It was NOT collapsed here, because that file sits outside this
// session's before-capture and "unchanged" could not have been proven for it.
// Cheap and zero-risk whenever Invoicing is next opened.
export function ageDays(ts) {
  if (!ts) return 0;
  return Math.floor((Date.now() - new Date(ts)) / 86400000);
}

// #230. The Request history line read material_request_items[0]'s quantity while
// presenting itself as the whole request's history, so on any multi-item request
// it described one line and labelled it as all of them. It now states the scope
// instead. Deliberately not a per-item breakdown: both detail surfaces already
// render an "Items on this request" list directly above this line, so a breakdown
// here would print the same list twice on the same panel. Shared rather than
// restated on each surface — the divergence #12, #13 and #215 each cost a session
// to collapse started exactly this way.
export function requestedSummary(items) {
  const list = items || [];
  if (list.length > 1) return `${list.length} items`;
  const it = list[0];
  return `${it?.qty_requested ?? '—'} ${itemUom(it)}`.trim();
}

// An order that has been placed but whose vendor has not sent back a confirmation
// number yet. #415 adds a second way to record that number from the queue card;
// this predicate is what both surfaces agree on.
export function isAwaitingConfirmation(req) {
  return req?.status === 'ordered' && !String(req?.vendor_order_ref || '').trim();
}

// Validates a PO number against a company's configured mask, where '0' means any
// digit and 'A' means any letter and every other character must match itself.
// An empty format means the company has not configured one, which passes.
export function poNumberMatchesFormat(value, format) {
  if (!format) return true;
  const v = String(value || '');
  if (v.length !== format.length) return false;
  for (let i = 0; i < format.length; i++) {
    const f = format[i];
    const c = v[i];
    if (f === '0') { if (c < '0' || c > '9') return false; }
    else if (f === 'A') { if (!/[A-Za-z]/.test(c)) return false; }
    else if (c !== f) return false;
  }
  return true;
}

// The four branches, hardcoded. THIS IS ONE OF #252's THIRTEEN COPIES and moving
// it here does not fix that — it is hoisted out of PurchasingQueuePage.js only
// because four of that file's components need it once they live in separate
// files. #252 is hosted behind the January 2027 rollout and wants ONE list read
// from `branches`, not a fourteenth hardcoded copy in a tidier place.
export const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];
